import type { BrokerCapabilities } from "../services/brokerIntegrationApi";
import { isCustomCsvBrokerName } from "./customCsvAccountDisplay";
import { isHyperliquidBroker } from "./hyperliquidBroker";
import { isMt4Broker } from "./mt4Broker";
import { isMt5Broker } from "./mt5Broker";
import { isTradingViewBroker } from "./tradingViewBroker";
import { isTradingViewPaperBroker } from "./tradingViewPaperBroker";

export function normalizeBrokerSlug(brokerName: string): string {
  const bn = brokerName.trim().toLowerCase();
  return bn === "liquid" ? "liquidbrokers" : bn;
}

/** Brokers that never store live API credentials (import lane only). */
export function brokerNeverSupportsLiveSync(brokerName: string): boolean {
  const slug = normalizeBrokerSlug(brokerName);
  return (
    isImportOnlyBrokerName(slug) ||
    isHyperliquidBroker(slug) ||
    isTradingViewBroker(slug) ||
    isTradingViewPaperBroker(slug) ||
    isMt4Broker(slug) ||
    isMt5Broker(slug)
  );
}

/** CSV / statement-import brokers that list accounts on the CSV management tab (no live sync). */
export function isStatementImportAccountsBroker(brokerName: string | null | undefined): boolean {
  const slug = normalizeBrokerSlug(String(brokerName || ""));
  return (
    isHyperliquidBroker(slug) ||
    isTradingViewBroker(slug) ||
    isTradingViewPaperBroker(slug) ||
    isMt4Broker(slug) ||
    isMt5Broker(slug)
  );
}

export function statementImportAccountsManagementCopy(
  brokerName: string,
  displayName: string
): { cardDescription: string; accountsDescription: string } {
  const slug = normalizeBrokerSlug(brokerName);
  if (isHyperliquidBroker(slug)) {
    return {
      cardDescription: `Manage imported ${displayName} accounts. Use Import trades to upload another trade history CSV.`,
      accountsDescription: `Accounts imported from ${displayName} trade history CSV. Use Import trades above to add or update trades. Rename, clear trade data, or remove all Tessera data per account.`,
    };
  }
  if (isTradingViewBroker(slug)) {
    return {
      cardDescription: `Manage imported ${displayName} accounts. Use Import trades to upload another Strategy Tester CSV.`,
      accountsDescription: `Accounts imported from ${displayName} Strategy Tester CSV. Use Import trades above to add or update trades. Rename, clear trade data, or remove all Tessera data per account.`,
    };
  }
  if (isTradingViewPaperBroker(slug)) {
    return {
      cardDescription: `Manage imported ${displayName} accounts. Use Import trades to upload another Paper Order History CSV.`,
      accountsDescription: `Accounts imported from ${displayName} Order History CSV. Use Import trades above to add or update trades. Rename, clear trade data, or remove all Tessera data per account.`,
    };
  }
  if (isMt5Broker(slug)) {
    return {
      cardDescription: `Manage imported ${displayName} accounts. Use Import trades to upload another Trade History Report.`,
      accountsDescription: `Accounts imported from ${displayName} Trade History Report (.xlsx). Use Import trades above to add or update trades. Rename, clear trade data, or remove all Tessera data per account.`,
    };
  }
  if (isMt4Broker(slug)) {
    return {
      cardDescription: `Manage imported ${displayName} accounts. Use Import trades to upload another Detailed Report.`,
      accountsDescription: `Accounts imported from ${displayName} Detailed Report (.htm/.html). Use Import trades above to add or update trades. Rename, clear trade data, or remove all Tessera data per account.`,
    };
  }
  return {
    cardDescription: `Manage imported ${displayName} accounts. Use Import trades to upload more trades.`,
    accountsDescription: `Accounts imported for ${displayName}. Use Import trades above to add or update trades. Rename, clear trade data, or remove all Tessera data per account.`,
  };
}

export type BrokerCredentialRecord = Record<string, unknown> & {
  id?: string;
  broker_name?: string;
  account_code?: string;
  csv_import_only?: boolean;
  supports_live_sync?: boolean;
};

export type BrokerCredentialSyncFlags = {
  hasCredentialRecord: boolean;
  supportsLiveSync: boolean;
  csvImportOnly: boolean;
};

function parseBoolFlag(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

/**
 * Parse GET/POST /broker/credentials sync lane flags.
 * Live Broker Sync UI should gate on `supportsLiveSync`; CSV placeholder rows set `csvImportOnly`.
 */
export function parseBrokerCredentialSyncFlags(
  credentials: BrokerCredentialRecord | null | undefined,
  brokerName?: string
): BrokerCredentialSyncFlags {
  const hasCredentialRecord = Boolean(credentials);
  if (!hasCredentialRecord) {
    return { hasCredentialRecord: false, supportsLiveSync: false, csvImportOnly: false };
  }

  const explicitSupports = parseBoolFlag(credentials!.supports_live_sync);
  const explicitCsvOnly = parseBoolFlag(credentials!.csv_import_only);

  if (explicitSupports !== undefined || explicitCsvOnly !== undefined) {
    return {
      hasCredentialRecord: true,
      supportsLiveSync: explicitSupports === true,
      csvImportOnly: explicitCsvOnly === true,
    };
  }

  const slug = normalizeBrokerSlug(brokerName ?? String(credentials!.broker_name ?? ""));
  const importLaneOnly = brokerNeverSupportsLiveSync(slug);
  return {
    hasCredentialRecord: true,
    supportsLiveSync: !importLaneOnly,
    csvImportOnly: importLaneOnly,
  };
}

/**
 * True when opening the broker should not prefetch GET /broker/credentials.
 * CSV-only / statement-import lanes create credentials on first import (or optional save);
 * a 404 before that is expected and should not drive the UI.
 */
export function brokerSkipsCredentialsPrefetch(
  cap: BrokerCapabilities | null | undefined,
  brokerName?: string | null
): boolean {
  if (cap) {
    if (cap.auth?.supports_auto_sync === false) return true;
    const strategy = String(cap.trade_sync?.strategy || "").trim().toLowerCase();
    if (strategy === "csv_import_only") return true;
  }
  const slug = normalizeBrokerSlug(String(brokerName || cap?.broker_name || ""));
  return slug ? brokerNeverSupportsLiveSync(slug) : false;
}

/** Import-only lanes (Custom CSV, etc.) — never call GET /broker/accounts. */
export function isImportOnlyBrokerName(brokerName: string): boolean {
  return isCustomCsvBrokerName(normalizeBrokerSlug(brokerName));
}

/**
 * True when this broker supports live DXtrade-style account APIs
 * (GET /broker/accounts, broker account snapshot with refresh).
 */
export function brokerSupportsLiveBrokerAccountsApi(
  cap: Pick<BrokerCapabilities, "broker_name" | "auth"> | null | undefined
): boolean {
  if (!cap) return false;
  if (isImportOnlyBrokerName(cap.broker_name)) return false;
  return Boolean(cap.auth?.supports_auto_sync);
}

export function findBrokerCapability(
  capabilities: BrokerCapabilities[] | null | undefined,
  brokerName: string
): BrokerCapabilities | null {
  const slug = normalizeBrokerSlug(brokerName);
  return (
    (capabilities ?? []).find((cap) => normalizeBrokerSlug(cap.broker_name) === slug) ?? null
  );
}
